import { memo, useEffect, useMemo, useRef } from "react";
import type { ReactNode, UIEvent, ChangeEvent, FormEvent } from "react";
import NeoInput from "components/neo/Input";
import NeoIcon from "components/neo/Icon";
import ArrowSmallUpIcon from "assets/icons/ArrowSmallUp";
import ArrowPathIcon from "assets/icons/ArrowPath";
import { FormattedText } from "components/text/formatted-text";
import { useEvent } from "utils/use-event";
import cartStyles from "../cart/cart.module.scss";
// import AudioRecorder from "components/recorder/recorder";
// import MockMenu from "./mock-menu";
import QuickStartExamples from "./QuickStartExamples";
import RecipePanel from "../recipe/RecipePanel";
import liveChatStyles from "./live-chat.module.scss";
import React from "react";
import { TProduct } from "components/cart/cart-provider";
import { Recipe } from "types/recipe";

const MAX_WIDTH = 600;
const SCROLL_PADDING = 12;

export default function LiveChat({
  chatLoading,
  meta,
  isLoading,
  messages,
  onInputChange,
  onSubmit,
  input,
  onStop,
  onTranscription,
  onSearch,
  onSearchRecipe,
  showRecipePanel = true,
  serviceId,
}: {
  chatLoading?: boolean;
  meta?: ReactNode;
  isLoading: boolean;
  input: string;
  messages?: { role: "assistant" | "user"; content: string }[];
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onTranscription?: (transcription: string) => void;
  onSearch: (
    query: string,
    options: { serviceId: string }
  ) => Promise<TProduct[]> | TProduct[];
  onSearchRecipe: (
    query: string,
    options: { serviceId: string }
  ) => Promise<Recipe[]> | Recipe[];
  download?: boolean;
  onStop?: () => void;
  showRecipePanel?: boolean;
  serviceId: string;
}) {
  const submitHandler = useEvent((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isLoading) {
      onSubmit(event);
    }
  });

  const onStopInn = useEvent(() => {
    if (isLoading) {
      onStop?.();
    }
  });

  const handleExampleClick = useEvent((text: string) => {
    const fakeEvent = {
      target: { value: text },
      currentTarget: { value: text },
    } as ChangeEvent<HTMLInputElement>;
    onInputChange(fakeEvent);
  });

  const scrollPosition = useRef<HTMLSpanElement | null>(null);
  const scroll = useRef({ top: 0, auto: true });
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scroll.current.auto) {
      scrollPosition?.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleScroll = useEvent((e: UIEvent<HTMLDivElement>) => {
    const offset = SCROLL_PADDING * 2;
    const currentTop = e.currentTarget.scrollTop;
    scroll.current = {
      top: currentTop,
      auto:
        Math.round(currentTop + offset) >=
        e.currentTarget.scrollHeight - e.currentTarget.clientHeight,
    };
  });

  // --- Determine approximate height of the fixed bottom bar ---
  // This is tricky without rendering, using an estimate. Adjust as needed.
  // const BOTTOM_BAR_ESTIMATED_HEIGHT = 180; // Estimate in pixels

  return (
    <>
      {/* Main container - Adjust height if needed, maybe 100vh? */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          width: "100%",
          height: "100dvh" /* Dynamic viewport height for modern browsers */,
          overflow: "hidden" /* Prevent outer scroll */,
          /* Safari mobile fixes */
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        className={`${cartStyles["cart"]} ${cartStyles[serviceId]}`}
      >
        {!chatLoading ? (
          <>
            {/* 1. Scrollable Content Area (Chat, Carousel, My Selection) */}
            <div
              className={liveChatStyles.scrollableContentArea}
              ref={listRef} // Assign ref here if needed for scroll logic focus
              onScroll={handleScroll} // Assign scroll handler here
              style={{
                width: "100%",
                height: "100%", // Occupy full height initially
                overflowY: "auto", // Allows scrolling
                // Padding bottom to avoid overlap with fixed bar
                paddingBottom: `80px`,
              }}
            >
              {/* Inner container for centering content */}
              <div
                style={{
                  maxWidth: MAX_WIDTH,
                  margin: "0 auto",
                  padding: `0 ${SCROLL_PADDING}px`,
                }}
              >
                {/* Chat Messages */}
                <ChatMessages messages={messages as TMessages} />

                {/* Recipe Panel */}
                {showRecipePanel && (
                  <RecipePanel
                    onSearch={onSearch}
                    onSearchRecipe={onSearchRecipe}
                  />
                )}

                {meta}

                {/* Scroll anchor */}
                <span ref={scrollPosition}></span>
              </div>
            </div>
            {/* End Scrollable Content Area */}

            {/* --- 2. Fixed Bottom Bar --- */}
            <div className={liveChatStyles.fixedBottomBar}>
              {/* Quick Start Examples */}
              <QuickStartExamples onExampleClick={handleExampleClick} />

              {/* Form - Add margin: 0 style */}
              <form
                onSubmit={submitHandler}
                style={{ width: "100%", maxWidth: MAX_WIDTH, margin: 0 }}
              >
                <div style={{ display: "flex", gap: "5rem" }}>
                  <div className="meme-input__wrapper">
                    <div className="meme-input__recorder">
                      {/* <AudioRecorder onTranscription={onTranscription} /> */}
                    </div>
                    <NeoInput
                      value={input}
                      onChange={onInputChange}
                      placeholder="Type here"
                    />
                    <NeoIcon shadow={false} onClick={onStopInn}>
                      {isLoading ? (
                        <ArrowPathIcon rotation />
                      ) : (
                        <ArrowSmallUpIcon />
                      )}
                    </NeoIcon>
                  </div>
                </div>
              </form>
            </div>
            {/* --- End Fixed Bottom Bar --- */}
          </>
        ) : null}
      </div>
    </>
  );
}

type TMessages = { role: "user" | "assistant"; content: string; id?: string }[];

const ChatMessages = memo(({ messages }: { messages?: TMessages }) => {
  return (
    <>
      {messages?.map(({ content, role, id }, index) => (
        <ChatMessage
          key={id || `${role}-${index}`}
          text={content}
          isOwn={role === "user"}
        />
      ))}
    </>
  );
});

const ChatMessage = memo(
  ({ text, isOwn }: { text: string; isOwn: boolean }) => {
    const lines = useMemo(() => <FormattedText text={text} />, [text]);

    if (!text) {
      return null;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: !isOwn ? "flex-start" : "flex-end",
          alignItems: "center",
          width: "100%",
          minHeight: 0,
        }}
      >
        <div
          style={{
            maxWidth: "80%",
            margin: 0,
            textAlign: isOwn ? "end" : "start",
            padding: "8px 16px",
            color: isOwn ? "#272727" : "#D9D9D9",
            backgroundColor: isOwn ? "transparent" : "var(--primary-color)",
            borderRadius: isOwn ? "16px 16px 0px 16px" : "0 16px 16px 16px",
            border: isOwn ? "1px solid #272727" : "1px solid transparent",
          }}
        >
          {lines}
        </div>
      </div>
    );
  }
);
